import {useMemo} from 'react';
import type {BreadcrumbItem} from 'src/app/layout/BackBar';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {FormsDashViewType} from 'src/shared/constants/ViewTypes';

export type FormsBreadcrumbMode =
  | 'form-builder-create'
  | 'form-builder-edit'
  | 'form-list'
  | 'form-submit'
  | 'form-edit'
  | 'form-view';

export interface UseFormsBreadcrumbsProps {
  mode: FormsBreadcrumbMode;
  formDefinitionId?: string | number;
  formDefinitionName?: string;
  formSubmissionId?: string | number;
  formIdentifier?: string;
  fromTab?: FormsDashViewType;
}

export interface UseFormsBreadcrumbsResult {
  breadcrumbs: BreadcrumbItem[];
  fallbackTo: string;
}

export const useFormsBreadcrumbs = ({
  mode,
  formDefinitionId,
  formDefinitionName,
  formSubmissionId,
  formIdentifier,
  fromTab,
}: UseFormsBreadcrumbsProps): UseFormsBreadcrumbsResult => {
  return useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];
    let fallbackTo = PathUtility.FormsDashboard('user');

    // When coming from "All Forms" tab, use different breadcrumbs
    if (fromTab === 'all' && mode === 'form-view') {
      fallbackTo = PathUtility.FormsDashboard('all');
      crumbs.push({label: 'All Forms', to: PathUtility.FormsDashboard('all')});
      crumbs.push({label: formIdentifier || formDefinitionName || 'Form'});
      return {breadcrumbs: crumbs, fallbackTo};
    }

    switch (mode) {
      case 'form-builder-create': {
        fallbackTo = PathUtility.FormsDashboard('manage');
        crumbs.push({label: 'Forms', to: PathUtility.FormsDashboard('manage')});
        crumbs.push({label: 'New Form'});
        break;
      }

      case 'form-builder-edit': {
        fallbackTo = PathUtility.FormsDashboard('manage');
        crumbs.push({label: 'Forms', to: PathUtility.FormsDashboard('manage')});
        crumbs.push({label: formDefinitionName || 'Form'});
        break;
      }

      case 'form-list': {
        fallbackTo = PathUtility.FormsDashboard('user');
        crumbs.push({label: 'Forms', to: PathUtility.FormsDashboard('user')});
        crumbs.push({label: formDefinitionName || 'Form'});
        break;
      }

      case 'form-submit': {
        fallbackTo = formDefinitionId
          ? PathUtility.FormsList(formDefinitionId)
          : PathUtility.FormsDashboard('user');
        crumbs.push({label: 'Forms', to: PathUtility.FormsDashboard('user')});
        if (formDefinitionId) {
          crumbs.push({
            label: formDefinitionName || 'Form',
            to: PathUtility.FormsList(formDefinitionId),
          });
        }
        crumbs.push({label: 'New'});
        break;
      }

      case 'form-edit': {
        fallbackTo = formDefinitionId
          ? PathUtility.FormsList(formDefinitionId)
          : PathUtility.FormsDashboard('user');
        crumbs.push({label: 'Forms', to: PathUtility.FormsDashboard('user')});
        if (formDefinitionId) {
          crumbs.push({
            label: formDefinitionName || 'Form',
            to: PathUtility.FormsList(formDefinitionId),
          });
        }
        crumbs.push({label: 'Edit'});
        break;
      }

      case 'form-view': {
        fallbackTo = formDefinitionId
          ? PathUtility.FormsList(String(formDefinitionId))
          : PathUtility.FormsDashboard('user');
        crumbs.push({label: 'Forms', to: PathUtility.FormsDashboard('user')});
        if (formDefinitionId) {
          crumbs.push({
            label: formDefinitionName || 'Form',
            to: PathUtility.FormsList(String(formDefinitionId)),
          });
        }
        crumbs.push({label: formIdentifier || 'View'});
        break;
      }
    }

    return {breadcrumbs: crumbs, fallbackTo};
  }, [mode, formDefinitionId, formDefinitionName, formSubmissionId, formIdentifier, fromTab]);
};
